import 'leaflet-geosearch/dist/geosearch.css'
import 'leaflet/dist/leaflet.css'
import 'react-leaflet-draft'
import '../../assets/map.css'

import React from 'react';
import { Button, Card, Form, Col, Input, Row, Select, Spin, message, Modal, Descriptions, Typography, Flex } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { DELETE, GET } from '../../frameworks/HttpClient';
import { EditControl } from 'react-leaflet-draw';
import { MapContainer, FeatureGroup, LayerGroup, Marker, Popup, GeoJSON } from "react-leaflet";
import { URL_COMMON, URL_GEOGRAPHY, URL_PLANTATIONS } from '../../constants/urls';
import { LANGUAGE, GeographyLayer, PlantationPolygon, GROUPS } from '../../constants/string';
import { useRef } from 'react';
import L from 'leaflet'
import _ from "lodash";
import { useTranslation } from 'react-i18next';
import { PRIMARY_COLOR } from '../../constants/color';
import UploadFile from '../common/UploadFile';
import * as turf from '@turf/turf'
import * as wellknown from 'wellknown'
import { useTransition } from '../../hooks/useTransition';
import PolygonLayerSelect from './PlantationLocationView/PolygonLayerSelect'
import AddPolygonModal from './PlantationMap/AddPolygonModal'
import { DEFAULT_LOCATION, THAI_PROVINCE_POINT } from '../../constants/geos'
import LayerControl from '../map/layers/LayerControl'
import LayerOverlay from '../map/layers/overlays/LayerOverlay'
import { MarkerIcon, SearchMarkerIcon } from '../map/layers/icons/MarkerIcon'
import GeographyOverlay from './PlantationMap/overlays/GeographyOverlay'
import OverlapAreaOverlay from './PlantationMap/overlays/OverlapAreaOverlay'
import DolLayerOverlay from './PlantationMap/overlays/DolLayerOverlay'
import AlroLayerOverlay from './PlantationMap/overlays/AlroLayerOverlay'
import { useUserContext } from '../../hooks/UserSettingProvider'


const DEFAULT_ZOOM = 9
const DEFAULT_POLYGON_ZOOM = 17


const MapEditControl = React.memo(({ color, onCreated }) => {
  const onCreatedRef = React.useRef(onCreated)
  React.useEffect(() => { onCreatedRef.current = onCreated }, [onCreated])

  return (
    <EditControl
      onCreated={(e) => onCreatedRef.current?.(e)}
      draw={{
        polygon: { shapeOptions: { color }},
        marker: { icon: MarkerIcon },
        polyline: false,
        rectangle: false,
        circle: false,
        circlemarker: false
      }}
      edit={{
        edit: false,
        remove: false
      }}
    />
  )
})


const PlantationLocationView = React.forwardRef((props, ref) => {
  const mapRef = useRef();
  const [form] = Form.useForm();
  const { t, i18n } = useTranslation();
  const documentRef = React.useRef(null);

  const { user } = useUserContext()
  const isDolGroup = React.useMemo(() => user?.is_superuser || (user?.groups ?? []).includes(GROUPS.DOL), [user])

  const [subdistricts, setSubdistricts] = React.useState([]);
  const selectedSubdistrict = Form.useWatch('sub_district', form)
  const [selectedProvince, setSelectedProvince] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false)

  const [inputLat, setInputLat] = React.useState('');
  const [inputLng, setInputLng] = React.useState('');
  const [searchPosition, setSearchPosition] = React.useState(null);

  const [center, setCenter] = React.useState({location: DEFAULT_LOCATION, zoom: DEFAULT_ZOOM});
  const [layerType, setLayerType] = React.useState('total');
  const [deleteMode, setDeleteMode] =  React.useState(false);
  const [OpenAddPolygonModal, setOpenAddPolygonModal] = React.useState(false);
  const [polygons, setPolygons] = React.useState([]);
  const [deletedPolygon, setDeletedPolygon] = React.useState([]);
  const [point, setPoint] = React.useState(null);
  const [neighborPlotLayer, setNeighborPlotLayer] = React.useState([]);
  const [visibleLayers, setVisibleLayers] = React.useState(Object.values(PlantationPolygon.Types))
  const [visibleArea, setVisibleArea] = React.useState([])
  const [isLayerLoading, startLayerLoad] = useTransition()

  const generateSeqId = React.useCallback(() => {
    const uint32 = new Uint32Array(1);
    crypto.getRandomValues(uint32)
    return uint32[0]
  }, [])

  React.useImperativeHandle(ref, () => ({
    getData : handleRegister,
    getDeletedPolygon : () => deletedPolygon,
  }))

  const isMultiDeleteMode = React.useCallback((e) => e?.originalEvent?.shiftKey, [])

  const handleFlyTo = () => {
    const lat = parseFloat(inputLat);
    const lng = parseFloat(inputLng);

    if (!isNaN(lat) && !isNaN(lng)) {
      setSearchPosition([lat,lng])
      mapRef.current?.flyTo?.([lat, lng], DEFAULT_POLYGON_ZOOM);
    } else {
      message.error(t("please_enter_Latitude_Longitude"));
    }
  }

  const handleSubdistrictSearch = _.debounce((keyword) => {
    setIsLoading(true)
    GET(`${URL_COMMON.LOCATION_SEARCH}${keyword || ''}`, {page_size: 99})
      .then((response) => {
        setSubdistricts(
          response.data.results.map((item) => ({
            label: item.display_name,
            label_en: item.display_name_en,
            value: item.subdistrict_id
          })
        ))
      })
      .catch()
      .finally(() => setIsLoading(false))
  }, 500)

  const handleRegister = async () => {
    try {
      const data = await form.validateFields();
      data['point'] = point && { latitude: point.lat, longitude: point.lng }
      data['polygon'] = polygons.map(p => ({
        type: p.type,
        polygon: p.geojson.coordinates[0].map(([lng, lat]) => ({ latitude: lat, longitude: lng }))
      }))

      if (documentRef.current) {
        const documentData = await documentRef.current.getData()
        data["additional_document"] = documentData ? documentData : [];
      }

      // finally is triggered just before return so we need to add polygon to data before returning
      return data
    }
    catch (error) { }
    finally {
      form.resetFields();
      setPolygons([]);
      console.debug(1)
    }
  }

  const checkLayerOverlap = (data) => {
    const getResults = async () => {
      const geom = wellknown.stringify(data)
      return await Promise.all([
        GET(URL_GEOGRAPHY.CHECK_OVERLAP, { geom }).then(resp => resp.data).catch(() => []),
        GET(URL_PLANTATIONS.CHECK_OVERLAP, { geom, exclude_id: props.data?.id }).then(resp => resp.data).catch(() => [])
      ])
    }

    startLayerLoad(async () => {
      const results = await getResults()
      if (!(results[0].length || results[1].length)) return
      Modal.warning({
        title: 'Overlap Layer Detected',
        content: (
          <ul>
            {results[0].map((layer) => {
              return (
                <li>
                  {`${layer.name} (${t(GeographyLayer.Labels?.[layer.type]) || 'ไม่ทราบประเภท'})`}
                </li>
              )
            })}
            {results[1].map((layer) => (
              <li>{`${layer?.code || 'แปลงรอการอนุมัติ'} (${layer?.farmer_name || 'ไม่ทราบ'})`}</li>
            ))}
          </ul>
        )
      })
    })
  }

  const fetchNeighborPlotLayer = () => {
    startLayerLoad(async () => {
      setNeighborPlotLayer([])
      
      if(props.polygonLayer.length > 0){
        const results = await Promise.all(props.polygonLayer.map((layer) => (
          GET(
            `${URL_PLANTATIONS.NEIGHBOR_PLOT}`,
            {
              radius: 2.5,
              longitude: layer.centroid.longitude,
              latitude: layer.centroid.latitude,
              page_size : 999
            }
          )
          .then((response) => response.data.results.filter(item => item.id !== props.data.id))
          .catch(() => [])
        )))
        setNeighborPlotLayer(_(results).flatten().uniqBy(({ id }) => id).value())
      }
      else {
        const response = await GET(
          `${URL_PLANTATIONS.NEIGHBOR_PLOT}`,
          {
            radius: 5,
            longitude: center.location[1],
            latitude: center.location[0],
            page_size : 999
          }
        )
        setNeighborPlotLayer(response.data.results)
      }
    })
  }

  const getAreaText = (area) => {
    const rai = (area ?? 0) / 1600
    const ngan = (rai%1) * 4
    const sqwa = (ngan%1) * 100

    return `${Math.floor(rai).toLocaleString()} ${t('rai').toLowerCase()} ${Math.floor(ngan)} ${t('ngan').toLowerCase()} ${sqwa.toFixed(4)} ${t('square_wah').toLowerCase()}`
  }

  const getMaxArea = React.useMemo(() => (
    (props?.polygonLayer ?? [])
      .filter(({ type }) => type === PlantationPolygon.Types.TOTAL)
      .reduce((acc, { area }) => Math.max(area, acc), 0)
  ), [props.polygonLayer])

  const polygonFilterPredicate = React.useCallback((item) => (visibleLayers.includes(item.type) && !deletedPolygon.includes(item.id)), [visibleLayers, deletedPolygon])
  const renderPolygon = (item) => {
    return (
      <GeoJSON
        key={item.id}
        data={item.geojson}
        style={{ color: PlantationPolygon.Colors[item.type] }}
        eventHandlers={{
          click(e) {
            if (deleteMode) {
              setPolygons(polygons => polygons.filter(polygon => polygon.id !== item.id))
              if (!isMultiDeleteMode(e)) setDeleteMode(false);
              
              if (props?.polygonLayer && props.polygonLayer.length && props.polygonLayer.find(p => p.id === item.id)) {
                setDeletedPolygon(prevState => ([...prevState, item.id]))
              }
            }
          },
        }}
      >
        {!deleteMode && (
          <Popup>
            <Flex vertical>
              <Typography.Text><b>{t('type')}: </b>{t(PlantationPolygon.Labels[item.type])}</Typography.Text>
              <Typography.Text><b>{t('area')}: </b>{getAreaText(item.area)}</Typography.Text>
            </Flex>
          </Popup>
        )}
      </GeoJSON>
    )
  }

  const addVisibleArea = React.useCallback((type, active) => {
    setVisibleArea(area => {
      const filtered = area.filter(a => a !== type)
      return active ? [...filtered, type] : filtered
    })
  }, [])

  const unionedGeometries = React.useMemo(() => {
    const geos = [
      ...polygons.filter(polygonFilterPredicate).map(p => turf.feature(p.geojson)),
      ...(Array.isArray(props?.polygonLayer) ? props.polygonLayer : []).filter(polygonFilterPredicate).map(p => turf.feature(p.geojson)),
    ]

    if (geos.length <= 0) return null;
    const unioned = geos.length > 1 ? turf.union(turf.featureCollection(geos)) : geos[0]
    return wellknown.stringify(unioned)
  }, [polygons, props.polygonLayer, polygonFilterPredicate])

  const handlePolygonCreate = (e) => {
    if (e.layerType === "polygon") {
      const polygon = e.layer.toGeoJSON().geometry
      const newPolygon = { id: generateSeqId(), type: layerType, geojson: polygon, area: turf.area(polygon) }
      setPolygons(prevPolygons => ([...prevPolygons, newPolygon]))
      checkLayerOverlap(polygon)
      mapRef.current.removeLayer(e.layer)
    }
    else if (e.layerType === "marker") {
      setPoint(e.layer._latlng)
      checkLayerOverlap(e.layer.toGeoJSON())
      mapRef.current.removeLayer(e.layer)
    }
  }

  const handleCoordsImported = (type, polygons) => {
    if (polygons.length) {
      const newPolygons = polygons.map(p => ({ id: generateSeqId(), type: type, geojson: p, area: turf.area(p) }))
      setPolygons(prevPolygons => ([...prevPolygons, ...newPolygons]))

      // Check overlap layer with backend
      const gc = turf.geometryCollection(polygons)
      checkLayerOverlap(gc)
      mapRef.current?.flyToBounds?.(L.geoJSON(gc).getBounds())
    }
  }

  React.useEffect(() => {
    form.setFieldsValue(props.data)
    if (props.data?.sub_district) {
      setSubdistricts([{
        label: props.data.sub_district_detail,
        label_en: props.data.sub_district_detail_en,
        value: props.data.sub_district
      }])
    }
  }, [form, props.data])

  React.useEffect(() => {
    let center = { location: DEFAULT_LOCATION, zoom: DEFAULT_ZOOM }
    if (props.data?.point) {
      setPoint({ lat: props.data.point.latitude, lng: props.data.point.longitude })
      center = { location: [props.data.point.latitude, props.data.point.longitude], zoom: DEFAULT_POLYGON_ZOOM }
    }

    if (!(props?.polygonLayer && props.polygonLayer.length)) {
      setCenter(center)
    }
  }, [props.data, props.polygonLayer])

  React.useEffect(() => {
    if (!selectedSubdistrict) setSelectedProvince(null);

    GET(URL_COMMON.PROVINCE, { 'sub_district': selectedSubdistrict })
      .then(resp => setSelectedProvince(resp.data?.[0] ?? null))
  }, [selectedSubdistrict])

  React.useEffect(() => {
    // check if map is empty
    if (point || polygons.length) return;
    if (Array.isArray(props?.polygonLayer)) {
      if (props.polygonLayer.filter(layer => !deletedPolygon.includes(layer.id)).length) {
        return;
      }
    }
    
    if (selectedProvince) {
      const location = THAI_PROVINCE_POINT?.[selectedProvince?.name_th] ?? DEFAULT_LOCATION
      setCenter({location: location, zoom: DEFAULT_ZOOM})
      mapRef.current?.flyTo?.(location, DEFAULT_ZOOM)
    }
  }, [selectedProvince])

  React.useEffect(() => {
    if (deleteMode) {
      mapRef.current?.boxZoom?.disable?.()
    }
    else {
      mapRef.current?.boxZoom?.enable?.()
    }
  }, [deleteMode])

  return (
    <>
      <Card>
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
        >
          <Row gutter={[16, 16]}>
            <Col md={14} sm={24} xs={24}>
              <Form.Item
                name={"address"}
                label={t("the_location_of_the_rubber_plantation_village_number")}
              >
                <Input autoComplete="off"/>
              </Form.Item>
            </Col>

            <Col md={10} sm={24} xs={24}>
              <Form.Item
                name={"sub_district"}
                label={t("farmers.subdistrict_district_province_zip_code")}
              >
                <Select
                  autoComplete="off"
                  showSearch
                  filterOption={false}
                  notFoundContent={isLoading ? <Spin size="small" /> : "-"}
                  onSearch={handleSubdistrictSearch}
                  options={subdistricts.map(({label, label_en, value}) => ({value, label: i18n.language === LANGUAGE.TH ? label : label_en}))}
                  allowClear={"-"}
                />
              </Form.Item>
            </Col>

            <Col md={14} sm={24} xs={24}>
              <Form.Item
                name = 'note'
                label = {t("description_of_the_area")}
              >
                <TextArea autoComplete='off' placeholder={t("description")} rows={3}/>
              </Form.Item>
            </Col>

            <Col md={10} sm={24} xs={24}>
              <Form.Item
                name="additional_document"
                label={t("additional_document")}
              >
                <UploadFile
                  className={"upload-list-inline"}
                  style={{ background: PRIMARY_COLOR, color: 'white' }}
                  btnText={t('additional_document_upload')}
                  maxCount={1}
                  ref={documentRef}
                  dataList={props.additional_document ? props.additional_document : null}
                  onRemove={(data) => DELETE(`${URL_PLANTATIONS.DOCUMENT}${data.id}/`)}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        {selectedSubdistrict && (
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col md={4} sm={24} xs={24}>
              <Input
                type="number"
                placeholder="latitude"
                name="lat"
                value={inputLat}
                min={-90}
                max={90}
                onChange={(e) => setInputLat(e.target.value)}
                addonBefore="lat"
              />
            </Col>
            <Col md={4} sm={24} xs={24}>
              <Input
                type="number"
                placeholder="longitude"
                name="lng"
                value={inputLng}
                min={-180}
                max={180}
                onChange={(e) => setInputLng(e.target.value)}
                addonBefore="lng"
              />
            </Col>
            <Col flex='none'>
              <Button onClick={handleFlyTo}>{t("search")}</Button>
            </Col>

            <Col flex='auto'/>

            {/* Dropdown Polygon Type */}
            <Col md={6} sm={24} xs={24}>
              <PolygonLayerSelect
                style={{ width : "100%" }}
                visibleLayers={visibleLayers}
                onVisibleChange={layers => setVisibleLayers(layers)}
                value={layerType}
                onChange={e => {
                  setLayerType(e)
                  setDeleteMode(false)
                }}
              />
            </Col>

            <Col flex='none'>
              <Button
                danger
                type={deleteMode ? "dashed" :  "primary" }
                onClick={() => setDeleteMode((currentMode) => !currentMode)}
              >
                {deleteMode ? t("cancel") : t("actions.delete_data")}
              </Button>
            </Col>
            <Col flex='none'>
              <Button type="primary" onClick={() => setOpenAddPolygonModal(true)}>{t("polygon_coordinates")}</Button>
            </Col>

            <Col span={24}>
              <Spin spinning={isLayerLoading}>
                <div style={{height: 600, position: 'relative'}}>
                  <MapContainer
                    maxZoom={20}
                    zoom={center.zoom}
                    center={center.location}
                    ref={mapRef}
                    attributionControl={false}
                  >
                    <LayerControl defaultLayer='gmap_satellite'>
                      <Flex vertical>
                        <GeographyOverlay
                          type={GeographyLayer.Types.DE_FOREST}
                          province={selectedProvince}
                          onSelect={(active) => addVisibleArea(GeographyLayer.Types.DE_FOREST, active)}
                          onLoad={startLayerLoad}
                        />
                        <GeographyOverlay
                          type={GeographyLayer.Types.NATIONAL_PARK}
                          onSelect={(active) => addVisibleArea(GeographyLayer.Types.NATIONAL_PARK, active)}
                          onLoad={startLayerLoad}
                        />
                        <GeographyOverlay
                          type={GeographyLayer.Types.NON_HUNTING}
                          province={selectedProvince}
                          onSelect={(active) => addVisibleArea(GeographyLayer.Types.NON_HUNTING, active)}
                          onLoad={startLayerLoad}
                        />
                        <GeographyOverlay
                          type={GeographyLayer.Types.PROTECTED_AREA}
                          province={selectedProvince}
                          onSelect={(active) => addVisibleArea(GeographyLayer.Types.PROTECTED_AREA, active)}
                          onLoad={startLayerLoad}
                        />
                        <GeographyOverlay
                          type={GeographyLayer.Types.FOREST_COVER}
                          province={selectedProvince}
                          onSelect={(active) => addVisibleArea(GeographyLayer.Types.FOREST_COVER, active)}
                          onLoad={startLayerLoad}
                        />
                        <GeographyOverlay
                          type={GeographyLayer.Types.RESERVED_FOREST}
                          province={selectedProvince}
                          onSelect={(active) => addVisibleArea(GeographyLayer.Types.RESERVED_FOREST, active)}
                          onLoad={startLayerLoad}
                        />
                      </Flex>

                      <Flex vertical>
                        <OverlapAreaOverlay geometry={unionedGeometries} types={visibleArea} onLoad={startLayerLoad} />
                        <LayerOverlay
                          name='neighbor'
                          title={<span style={{ color: GeographyLayer.Colors.NEIGHBOR_PLOT }}>{t(GeographyLayer.Labels.NEIGHBOR_PLOT)}</span>}
                          onChange={checked => {
                            if (checked) {
                              fetchNeighborPlotLayer()
                            }
                            else {
                              setNeighborPlotLayer([])
                            }
                          }}
                        >
                          {neighborPlotLayer.map((layer) => (
                            <GeoJSON style={{ color: GeographyLayer.Colors.NEIGHBOR_PLOT }} data={layer.geojson}>
                              <Popup>
                                {`[${layer?.code || 'แปลงรอการอนุมัติ' }]`}<br />
                                {layer?.farmer_name || 'ไม่ทราบ'}
                              </Popup>
                            </GeoJSON>
                          ))}
                        </LayerOverlay>
                      </Flex>

                      {isDolGroup && (
                        <Flex vertical>
                          <DolLayerOverlay/>
                          <AlroLayerOverlay province={selectedProvince}/>
                        </Flex>
                      )}
                    </LayerControl>
                    
                    <FeatureGroup>
                      <MapEditControl
                        color={PlantationPolygon.Colors[layerType]}
                        onCreated={handlePolygonCreate}
                      />
                      
                      {/* Created Polygon */}
                      <LayerGroup>{polygons.filter(polygonFilterPredicate).map((layer) => renderPolygon(layer))}</LayerGroup>

                      {/* Render fetch polygon type total */}
                      {Array.isArray(props?.polygonLayer) && props.polygonLayer.length && (
                        <FeatureGroup
                          eventHandlers={{
                            add(e) {
                              try {
                                const map = mapRef.current?.fitBounds?.(e?.target?.getBounds?.())
                                setCenter({ location: map.getCenter(), zoom: map.getZoom() })
                              }
                              catch(_) {}
                            }
                          }}
                        >
                          {props.polygonLayer.filter(polygonFilterPredicate).map((layer) => renderPolygon(layer))}
                        </FeatureGroup>
                      )}
                    </FeatureGroup>

                    {searchPosition && (
                      <Marker
                        position={searchPosition}
                        icon={SearchMarkerIcon}
                        eventHandlers={{
                          click (e) {
                            if (deleteMode) {
                              mapRef.current.removeLayer(e.target);
                              if (!isMultiDeleteMode(e)) setDeleteMode(false);
                              setSearchPosition(null);
                            }
                          }
                        }}
                      />
                    )}

                    {point && (
                      <Marker
                        position={ point }
                        icon={MarkerIcon}
                        eventHandlers={{
                          click: (e) => {
                            if(deleteMode){
                              mapRef.current.removeLayer(e.target);
                              setPoint(null);
                              if (!isMultiDeleteMode(e)) setDeleteMode(false);
                            }
                          }
                        }}
                      />
                    )}
                  </MapContainer>

                  <Descriptions
                    bordered
                    className='solid-bg tiny'
                    items={[{label: <b>{t('area')}</b>, children: getAreaText(getMaxArea)}]}
                    style={{ position: 'absolute', left: 12, bottom: 12, maxWidth: 'calc(100% - 24px)' }}
                  />
                </div>
              </Spin>
            </Col>
          </Row>
        )}
      </Card>

      <AddPolygonModal
        open={OpenAddPolygonModal}
        onClose={() => setOpenAddPolygonModal(false)}
        onImport={handleCoordsImported}
        data={props.polygonLayer}
      />
    </>
  )
})

export default React.memo(PlantationLocationView)
